import { makeAutoObservable, runInAction } from 'mobx'
import React from 'react'
import { Call } from '@twilio/voice-sdk'
import { type ActionItem } from 'shared/ui'
import { logger } from 'shared/lib'
import { ZIndex } from 'shared/constants/zIndex'
import { type IParamsCreateMessage } from 'entities/Message'
import { Conversation, type IParamsConversationCreate } from 'entities/Conversation'
import { EnumVariantMessageField, MessageFieldStore, EmojiAction } from 'widgets/MessageField'
import { type CallPopUpInsightCardsStore } from 'widgets/CallPopUp/store/callPopUpInsightCardsStore'

export class CallNoteStore {
  private _messageFieldStore: MessageFieldStore
  private _paramsMessage: IParamsCreateMessage | null = null
  private _paramsConversation: IParamsConversationCreate | null = null
  private _messageSended = false
  private _lastPreparedCallSid: string | null = null

  constructor(private _callPopUpInsightCardsStore: CallPopUpInsightCardsStore) {
    makeAutoObservable(this)

    this._messageFieldStore = new MessageFieldStore(
      {
        placeholder: 'Write call note…',
        noCounter: true,
        showActionsItems: 1,
        textLimit: {
          maxLength: 1600,
          message: 'Note can contain max 1600 characters',
          showNotification: true,
        },
        variant: EnumVariantMessageField.Note,
      },
      this._callPopUpInsightCardsStore.entities
    )
    this.setActions()
    this._messageFieldStore.setFocusMessageFieldTrigger()
  }

  private _template = 'Call notes'

  setActions = () => {
    this._messageFieldStore?.setActions(this.getMessageFieldActions())
  }

  setFocusMessageField = () => {
    this._messageFieldStore.setFocusMessageFieldTrigger()
  }

  getMessageFieldActions = () => {
    const actions: (ActionItem | null)[] = []

    actions.push({
      iconButtonComponent: <EmojiAction zIndex={ZIndex.OVERLAY_TOP_1} />,
    })

    return actions.filter((action) => !!action) as ActionItem[]
  }

  get entities() {
    return this._callPopUpInsightCardsStore.entities
  }

  get callStore() {
    return this._callPopUpInsightCardsStore.callStore
  }

  get conversationStore() {
    return this._callPopUpInsightCardsStore.conversationStore
  }

  handleMessageSend = async () => {
    if (
      !this.paramsConversation ||
      !this.paramsMessage ||
      this.callPopUpInsightCardsStore.showCallTypesModal ||
      this.callStore.status !== Call.State.Closed ||
      this._messageSended
    ) {
      return
    }

    try {
      runInAction(() => {
        this._messageSended = true
      })

      const conversation = await this.conversationStore.createConversation(this.paramsConversation)

      if (conversation instanceof Conversation) {
        await this.entities.messageStore.createMessageByConversationId(
          conversation.id,
          this.paramsMessage
        )
      }
    } catch (e) {
      logger.error(e)
    }
  }

  prepareMessageData = () => {
    if (!this.callStore.connect) return
    if (!this.callStore.contactTo) return
    if (!this.callStore.contactFrom) return

    const currentCallSid = this.callStore.connect.parameters.CallSid

    if (this._lastPreparedCallSid !== currentCallSid) {
      this._messageSended = false
      this._lastPreparedCallSid = currentCallSid
    }

    this.callPopUpInsightCardsStore?.setNoteText(this._messageFieldStore.text)

    if (!this._messageFieldStore.text) {
      this._paramsMessage = null
      this._paramsConversation = null

      return
    }

    this._paramsMessage = {
      media_url: [],
      manual_created: true,
      message: this.template,
      send_at: '',
      stop_on_response: false,
      type: 'note',
      should_save_draft: true,
      call_sid: this.callStore.connect.parameters.CallSid,
      sub_source: null,
    }

    this._paramsConversation = {
      contact_id: this.callStore.contactTo.id,
      team_id: this.callStore.contactFrom.id,
    }
  }

  setMessageFieldData = () => {
    this.messageFieldStore.setMessageData({
      attachments: [],
      message: this.callPopUpInsightCardsStore.noteText ?? '',
      isFocus: true,
      isReset: false,
      isReplace: true,
    })
  }

  get paramsMessage() {
    return this._paramsMessage
  }

  get paramsConversation() {
    return this._paramsConversation
  }

  get template() {
    return `${this._template}\n\n ${this._messageFieldStore.text}`
  }

  get messageFieldStore() {
    return this._messageFieldStore
  }

  get callPopUpInsightCardsStore() {
    return this._callPopUpInsightCardsStore
  }
}
